import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Home,
  Plus,
  Edit,
  Trash2,
  X,
  Search,
  Shield,
  Calendar,
  Users,
  MapPin,
  Activity,
} from "lucide-react";

/**
 * RemoteWorkPolicy.jsx
 * - Zoho / Keka style admin page
 * - Fonts: Poppins + Roboto
 * - Right-side drawer for Create / Edit
 * - Fully functional (local state) — easy to wire to API
 *
 * Save as: src/pages/RemoteWorkPolicy.jsx
 */

export default function RemoteWorkPolicy() {
  // ---------- Mock data ----------
  const [policies, setPolicies] = useState(() => [
    {
      id: 1,
      name: "Hybrid - Core Office 3 Days",
      description: "Employees may work remotely up to 2 days/week. Core days Mon/Tue/Wed in office.",
      applyTo: ["All Employees"],
      published: true,
      allowedRemoteDaysPerWeek: 2,
      minConsecutiveRemoteDays: 0,
      maxConsecutiveRemoteDays: 2,
      allowedWeekdays: [1, 4], // Mon(1) & Thu(4) — sample allowed remote days list (only illustrative)
      requiresApproval: true,
      approvalFlow: "Manager -> HR",
      deviceRestriction: true,
      geofenceRequired: false,
      monthlyQuota: 8,
      effectiveFrom: "2025-02-01",
      exceptions: [{ id: "ex1", title: "Sales - Field", notes: "Field sales allowed weekly 4 days" }],
      lastUpdated: new Date().toISOString(),
    },
    {
      id: 2,
      name: "Fully Remote - Distributed Team",
      description: "Distributed team may work remotely full-time subject to manager approval.",
      applyTo: ["Remote Team"],
      published: false,
      allowedRemoteDaysPerWeek: 7,
      minConsecutiveRemoteDays: 1,
      maxConsecutiveRemoteDays: 30,
      allowedWeekdays: [0,1,2,3,4,5,6],
      requiresApproval: true,
      approvalFlow: "Manager",
      deviceRestriction: false,
      geofenceRequired: false,
      monthlyQuota: 999,
      effectiveFrom: "2025-01-01",
      exceptions: [],
      lastUpdated: new Date().toISOString(),
    },
  ]);

  // ---------- UI state ----------
  const [search, setSearch] = useState("");
  const [filterPublished, setFilterPublished] = useState("all"); // all / published / draft
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(getEmptyForm());
  const [groups] = useState(["All Employees", "Remote Team", "Sales", "Engineering", "HR"]);
  const [locations] = useState(["Head Office", "Branch A", "Branch B"]);
  const fileRef = useRef(null);

  // close drawer on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && drawerOpen) closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  // derived filtered policies
  const filteredPolicies = useMemo(() => {
    const q = search.trim().toLowerCase();
    return policies.filter((p) => {
      if (filterPublished === "published" && !p.published) return false;
      if (filterPublished === "draft" && p.published) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        (p.applyTo || []).join(" ").toLowerCase().includes(q)
      );
    });
  }, [policies, search, filterPublished]);

  // ---------- helpers ----------
  function getEmptyForm() {
    return {
      id: null,
      name: "",
      description: "",
      applyTo: [],
      published: false,
      allowedRemoteDaysPerWeek: 2,
      minConsecutiveRemoteDays: 0,
      maxConsecutiveRemoteDays: 5,
      allowedWeekdays: [0,1,2,3,4,5,6],
      requiresApproval: true,
      approvalFlow: "Manager",
      deviceRestriction: false,
      geofenceRequired: false,
      monthlyQuota: 8,
      effectiveFrom: isoDate(new Date()),
      exceptions: [],
    };
  }

  function isoDate(d) {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  function openCreate() {
    setEditing(null);
    setForm(getEmptyForm());
    setDrawerOpen(true);
    if (fileRef.current) fileRef.current.value = "";
  }

  function openEdit(p) {
    setEditing(p);
    // deep clone to avoid accidental mutates
    setForm(JSON.parse(JSON.stringify(p)));
    setDrawerOpen(true);
    if (fileRef.current) fileRef.current.value = "";
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditing(null);
    setForm(getEmptyForm());
  }

  function savePolicy(e) {
    e?.preventDefault();
    if (!form.name.trim()) { alert("Please add a Policy Name"); return; }
    // basic validation: allowedRemoteDaysPerWeek between 0..7
    if (form.allowedRemoteDaysPerWeek < 0 || form.allowedRemoteDaysPerWeek > 7) {
      alert("Allowed remote days per week must be between 0 and 7");
      return;
    }

    if (editing) {
      setPolicies((prev) => prev.map((x) => (x.id === editing.id ? { ...form, lastUpdated: new Date().toISOString() } : x)));
    } else {
      const newPolicy = { ...form, id: Date.now(), lastUpdated: new Date().toISOString() };
      setPolicies((prev) => [newPolicy, ...prev]);
    }

    closeDrawer();
  }

  function deletePolicy(id) {
    if (!window.confirm("Delete this policy? This cannot be undone.")) return;
    setPolicies((prev) => prev.filter((p) => p.id !== id));
  }

  function togglePublish(id) {
    setPolicies((prev) => prev.map((p) => (p.id === id ? { ...p, published: !p.published, lastUpdated: new Date().toISOString() } : p)));
  }

  // manage applyTo groups toggling
  function toggleGroupInForm(group) {
    setForm((f) => {
      const s = new Set(f.applyTo || []);
      if (s.has(group)) s.delete(group); else s.add(group);
      return { ...f, applyTo: Array.from(s) };
    });
  }

  // manage allowed weekdays toggle
  function toggleWeekdayInForm(dayIndex) {
    setForm((f) => {
      const s = new Set(f.allowedWeekdays || []);
      if (s.has(dayIndex)) s.delete(dayIndex); else s.add(dayIndex);
      return { ...f, allowedWeekdays: Array.from(s).sort() };
    });
  }

  // exceptions management
  function addException() {
    const id = `ex_${Date.now()}`;
    setForm((f) => ({ ...f, exceptions: [...(f.exceptions||[]), { id, title: "", notes: ""}] }));
  }
  function updateException(idx, key, value) {
    setForm((f) => {
      const ex = [...(f.exceptions||[])];
      ex[idx] = { ...ex[idx], [key]: value };
      return { ...f, exceptions: ex };
    });
  }
  function removeException(idx) {
    setForm((f) => {
      const ex = [...(f.exceptions||[])];
      ex.splice(idx,1);
      return { ...f, exceptions: ex };
    });
  }

  // weekday labels
  const WEEK_DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  // ---------- rendering ----------
  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-['Poppins','Roboto',sans-serif]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3">
              <Home className="text-teal-600" /> Remote Work Policy Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">Create and manage policies for remote work — eligibility, quotas, restrictions and approval flows.</p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white border rounded-lg px-3 py-2 shadow">
              <Search size={16} className="text-gray-400 mr-2" />
              <input placeholder="Search policies..." className="outline-none w-56" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>

            <select className="border rounded-lg p-2 bg-white" value={filterPublished} onChange={(e) => setFilterPublished(e.target.value)}>
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>

            <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg shadow hover:bg-teal-700">
              <Plus size={14} /> New Policy
            </button>
          </div>
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main list */}
          <div className="lg:col-span-3 space-y-4">
            {filteredPolicies.length === 0 ? (
              <div className="bg-white p-6 rounded-lg shadow text-center text-gray-500">No policies found.</div>
            ) : (
              filteredPolicies.map((p) => (
                <div key={p.id} className="bg-white p-5 rounded-lg shadow flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{p.name}</h3>
                        <div className="text-sm text-gray-500 mt-1">{p.description}</div>

                        <div className="flex items-center gap-3 text-sm text-gray-600 mt-3">
                          <div>Allowed / week: <strong className="text-gray-800 ml-1">{p.allowedRemoteDaysPerWeek}</strong></div>
                          <div>Monthly quota: <strong className="text-gray-800 ml-1">{p.monthlyQuota}</strong></div>
                          <div>Approval: <strong className="text-gray-800 ml-1">{p.approvalFlow || (p.requiresApproval ? "Manager" : "None")}</strong></div>
                        </div>

                        <div className="mt-3 text-xs text-gray-500">
                          Applies to: {(p.applyTo||[]).join(", ") || "—"}
                        </div>
                      </div>

                      <div className="md:hidden">
                        {/* small actions for narrow screens */}
                        <button onClick={() => openEdit(p)} className="text-indigo-600 mr-2"><Edit size={16} /></button>
                      </div>
                    </div>
                  </div>

                  {/* right column actions */}
                  <div className="flex flex-col items-end gap-3">
                    <div className="flex items-center gap-2">
                      <button className={`px-3 py-1 rounded text-sm border ${p.published ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-700"}`} onClick={() => togglePublish(p.id)}>
                        {p.published ? "Published" : "Draft"}
                      </button>
                      <button onClick={() => openEdit(p)} className="px-3 py-1 border rounded text-sm text-indigo-600 flex items-center gap-2"><Edit size={14}/> Edit</button>
                      <button onClick={() => deletePolicy(p.id)} className="px-3 py-1 border rounded text-sm text-red-600 flex items-center gap-2"><Trash2 size={14}/> Delete</button>
                    </div>

                    <div className="text-xs text-gray-500">Last updated: {new Date(p.lastUpdated || p.createdAt || Date.now()).toLocaleString()}</div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Shield size={14} /> Quick Actions</h4>
                <div className="text-xs text-gray-500">{policies.length} policies</div>
              </div>

              <div className="mt-3 flex flex-col gap-2">
                <button onClick={() => { setPolicies([]); }} className="text-left px-3 py-2 border rounded text-sm">Clear All (demo)</button>
                <button onClick={() => alert("Exporting policies (demo)") } className="px-3 py-2 bg-teal-600 text-white rounded text-sm">Export Policies</button>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-semibold text-gray-700">Filters</h4>
              <div className="mt-3">
                <div className="text-xs text-gray-500 mb-2">Apply To</div>
                <div className="flex flex-wrap gap-2">
                  {groups.map((g) => {
                    const active = filteredPolicies.some((p) => (p.applyTo||[]).includes(g));
                    return <div key={g} className={`px-3 py-1 rounded text-sm ${active ? "bg-indigo-50 text-indigo-700" : "bg-gray-50 text-gray-700"}`}>{g}</div>;
                  })}
                </div>
              </div>

              <div className="mt-4">
                <div className="text-xs text-gray-500 mb-2">Locations</div>
                <div className="flex gap-2 flex-wrap">
                  {locations.map((l) => <div key={l} className="px-3 py-1 rounded bg-gray-50 text-gray-700 text-sm">{l}</div>)}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Drawer */}
      <Drawer open={drawerOpen} onClose={closeDrawer} title={editing ? "Edit Remote Work Policy" : "Create Remote Work Policy"}>
        <form onSubmit={savePolicy} className="space-y-4">
          {/* Name & Description */}
          <div>
            <label className="text-sm font-medium text-gray-700">Policy Name</label>
            <input className="w-full border rounded p-2 mt-1" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Description</label>
            <textarea rows={3} className="w-full border rounded p-2 mt-1" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
          </div>

          {/* Apply To - Groups */}
          <div>
            <label className="text-sm font-medium text-gray-700">Apply To (Groups)</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {groups.map((g) => {
                const active = (form.applyTo || []).includes(g);
                return (
                  <button key={g} type="button" onClick={() => toggleGroupInForm(g)} className={`px-3 py-1 rounded text-sm ${active ? "bg-teal-600 text-white" : "bg-white border text-gray-700"}`}>
                    {g}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Core settings grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-sm text-gray-700">Allowed Remote Days / Week</label>
              <input type="number" min="0" max="7" value={form.allowedRemoteDaysPerWeek} onChange={(e) => setForm({...form, allowedRemoteDaysPerWeek: Number(e.target.value)})} className="w-full border rounded p-2 mt-1" />
            </div>

            <div>
              <label className="text-sm text-gray-700">Monthly Quota (days)</label>
              <input type="number" min="0" value={form.monthlyQuota} onChange={(e) => setForm({...form, monthlyQuota: Number(e.target.value)})} className="w-full border rounded p-2 mt-1" />
            </div>

            <div>
              <label className="text-sm text-gray-700">Min / Max Consecutive Remote Days</label>
              <div className="flex gap-2 mt-1">
                <input type="number" min="0" value={form.minConsecutiveRemoteDays} onChange={(e) => setForm({...form, minConsecutiveRemoteDays: Number(e.target.value)})} className="w-1/2 border rounded p-2" />
                <input type="number" min="0" value={form.maxConsecutiveRemoteDays} onChange={(e) => setForm({...form, maxConsecutiveRemoteDays: Number(e.target.value)})} className="w-1/2 border rounded p-2" />
              </div>
            </div>
          </div>

          {/* Allowed weekdays */}
          <div>
            <label className="text-sm font-medium text-gray-700">Allowed Remote Weekdays</label>
            <div className="flex gap-2 flex-wrap mt-2">
              {WEEK_DAYS.map((wd, idx) => {
                const active = (form.allowedWeekdays || []).includes(idx);
                return <button key={wd} type="button" onClick={() => toggleWeekdayInForm(idx)} className={`px-3 py-1 rounded text-sm ${active ? "bg-indigo-600 text-white" : "bg-white border text-gray-700"}`}>{wd}</button>;
              })}
            </div>
          </div>

          {/* Approval & Restrictions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-700">Requires Approval</label>
              <div className="mt-2">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.requiresApproval} onChange={(e)=> setForm({...form, requiresApproval: e.target.checked})} /> Enable</label>
                {form.requiresApproval && (
                  <input className="w-full border rounded p-2 mt-2" value={form.approvalFlow} onChange={(e)=> setForm({...form, approvalFlow: e.target.value})} />
                )}
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-700">Device / Geofence Restrictions</label>
              <div className="flex flex-col gap-2 mt-2">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.deviceRestriction} onChange={(e)=> setForm({...form, deviceRestriction: e.target.checked})} /> Device Restriction</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.geofenceRequired} onChange={(e)=> setForm({...form, geofenceRequired: e.target.checked})} /> Require Geo-fence</label>
              </div>
            </div>
          </div>

          {/* Exceptions */}
          <div className="border rounded p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-700">Exceptions & Overrides</div>
              <button type="button" onClick={addException} className="px-2 py-1 text-sm bg-teal-600 text-white rounded">Add</button>
            </div>

            <div className="space-y-2">
              {(form.exceptions || []).length === 0 && <div className="text-sm text-gray-500">No exceptions defined.</div>}
              {(form.exceptions || []).map((ex, idx) => (
                <div key={ex.id} className="bg-white p-2 rounded border flex gap-2 items-center">
                  <input type="text" placeholder="Title (e.g. Sales Field)" value={ex.title} onChange={(e)=> updateException(idx, "title", e.target.value)} className="flex-1 border rounded p-2" />
                  <input type="text" placeholder="Notes" value={ex.notes || ""} onChange={(e)=> updateException(idx, "notes", e.target.value)} className="flex-1 border rounded p-2" />
                  <button type="button" onClick={()=> removeException(idx)} className="text-red-600 px-2"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Effective date + publish */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-700">Effective From</label>
              <input type="date" value={form.effectiveFrom} onChange={(e)=> setForm({...form, effectiveFrom: e.target.value})} className="w-full border rounded p-2 mt-1" />
            </div>

            <div>
              <label className="text-sm text-gray-700">Publish</label>
              <div className="mt-2">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.published} onChange={(e)=> setForm({...form, published: e.target.checked})} /> Publish policy</label>
              </div>
            </div>
          </div>

          {/* footer */}
          <div className="flex justify-end gap-3 pt-3">
            <button type="button" onClick={closeDrawer} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded">{editing ? "Update Policy" : "Create Policy"}</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
}

/* ---------------- Drawer component - same polished pattern used earlier ---------------- */
function Drawer({ open, onClose, title, children }) {
  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />

      <aside
        className={`fixed top-0 right-0 bg-white w-full md:w-3/5 lg:w-2/5 xl:w-1/3 h-full z-50 shadow-2xl transform transition-transform ${open ? "translate-x-0" : "translate-x-full"}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button onClick={onClose} aria-label="Close" className="p-2 rounded hover:bg-gray-100">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(100vh - 72px)" }}>
            {children}
          </div>
        </div>
      </aside>
    </>
  );
}
